/* eslint-disable no-undef */
import React, { useMemo } from "react";
import { mergeStyleSets, Modal } from "@fluentui/react";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { DocumentPartTree, TreeViewDataItem } from "./DocumentPartTree";
import { DocumentPartItem } from "../../redux/feature/document/document-slice";
import { formatMessage, formatMessageWithParams } from "../../utils/helpers/LanguageHelper";
import { openDocumentById } from "../../utils/helpers/OpenDocumentHelper";
import FooterPanel from "../layout/FooterPanel";
import HeaderPanel from "../layout/HeaderPanel";

export type DocumentPropertiesActionTypes =
  | "confirmNumberOfFathers"
  | "confirmChildrenDeletion"
  | "confirm"
  | "confirmUnlinkWillBePerformed";

export interface DocumentPropertiesMessagesProps {
  isModalOpen: boolean;
  onHideModal: any;
  onFathersConfirm?: (documentPart: DocumentPartItem, response: boolean) => Promise<void>;
  onConfirmDeletePart?: () => Promise<void>;
  deleteItem: DocumentPartItem;
  action: DocumentPropertiesActionTypes;
  fathersItemSelected: TreeViewDataItem[];
  numberOfChildren: number;
}

const contentStyles = mergeStyleSets({
  container: {
    display: "flex",
    flexFlow: "column nowrap",
    alignItems: "stretch",
    minHeight: "100vh",
    minWidth: "100vw",
  },
});

export const DocumentPropertiesMessagesModal: React.FunctionComponent<DocumentPropertiesMessagesProps> = (props) => {
  const {
    isModalOpen,
    onHideModal,
    onFathersConfirm,
    onConfirmDeletePart,
    deleteItem,
    action,
    fathersItemSelected,
    numberOfChildren,
  } = props;

  function modalHide() {
    onHideModal();
  }

  async function sendResponse() {
    try {
      if (action === "confirmUnlinkWillBePerformed") await onConfirmDeletePart();
      if (action === "confirmNumberOfFathers") await onFathersConfirm(deleteItem, true);
      if (action === "confirmChildrenDeletion") await onConfirmDeletePart();
      if (action === "confirm") await onConfirmDeletePart();
    } catch {
      modalHide();
    }
  }

  // eslint-disable-next-line no-undef
  console.log({ deleteItem, action });

  const { details, message, accentColor } = useMemo(() => {
    if (action === "confirmUnlinkWillBePerformed")
      return {
        message: formatMessage("cof.documentproperties.delete.message.request"),
        details: formatMessageWithParams("cof.documentproperties.delete.message.confirmunlink", {
          linkedPart: deleteItem?.name,
        }),
        accentColor: "var(--color-yellow)",
      };
    if (action === "confirmNumberOfFathers")
      return {
        message: formatMessage("cof.documentproperties.delete.message.request"),
        details: formatMessageWithParams("cof.documentproperties.delete.message", { linkedPart: deleteItem?.name }),
        accentColor: "var(--color-red)",
      };
    if (action === "confirmChildrenDeletion")
      return {
        message: formatMessageWithParams("cof.documentproperties.confirmdelete.message", {
          linkedPart: deleteItem?.name,
          numberOfChildren: numberOfChildren,
        }),
        accentColor: "var(--color-yellow)",
      };
    if (action === "confirm")
      return {
        message: formatMessage("cof.documentproperties.confirmdelete.closedocuments"),
        accentColor: "var(--color-yellow)",
      };
    return {};
  }, [deleteItem, numberOfChildren]);

  return (
    <Modal titleAriaId={"titleId"} isOpen={isModalOpen} containerClassName={contentStyles.container}>
      <HeaderPanel applyPadding borderColor={accentColor}>
        {formatMessageWithParams("cof.file.open.outline.title.documentoutline", {
          documentName: deleteItem?.name.replace(/\.docx$/, ""),
        })}
      </HeaderPanel>
      <div
        className="messageText"
        style={{
          margin: "12px 8px",
          padding: "8px 8px",
          borderRadius: "8px",
          backgroundColor: accentColor,
          color: "white",
        }}
      >
        {message}
      </div>
      {details && (
        <div className="bodyText" style={{ margin: "0 8px 12px 8px" }}>
          {details}
        </div>
      )}
      {action === "confirmNumberOfFathers" && fathersItemSelected && (
        <DocumentPartTree
          selectedPartId={undefined}
          rootParts={fathersItemSelected}
          expandIcons={false}
          openDocument={openDocumentById}
        />
      )}
      <FooterPanel>
        <PrimaryButton
          id="confirm_button"
          data-testid="scratch"
          onClick={() => sendResponse()}
          text={formatMessage("cof.base.yes")}
          style={{ float: "right" }}
        />
        <PrimaryButton
          id="cancel_button"
          text={formatMessage("cof.base.no")}
          style={{ float: "left" }}
          onClick={() => modalHide()}
        />
      </FooterPanel>
    </Modal>
  );
};
